'use client'

import React from 'react'
import {
  Package,
  AlertTriangle,
  ShieldCheck,
  ArrowDown,
  Search,
  Lock,
  Eye,
  FileWarning,
  Bot,
  Download,
  Skull,
  Bug,
} from 'lucide-react'

/**
 * Slopsquatting Diagrams
 * Visual components for the slopsquatting supply chain attacks blog post
 * Brand colors: Primary #008dcc, Accent #FDC304, Pink #F90B8A, Purple #8b5cf6
 */

/**
 * Slopsquatting Attack Flow Diagram
 */
export function SlopsquattingAttackFlowDiagram() {
  const steps = [
    {
      step: 1,
      label: 'AI Hallucinates Package',
      description: 'LLM recommends "react-auth-helper" (does not exist)',
      icon: Bot,
      color: '#8b5cf6',
    },
    {
      step: 2,
      label: 'Attacker Registers Name',
      description: 'Malicious actor claims the hallucinated package name',
      icon: Skull,
      color: '#ef4444',
    },
    {
      step: 3,
      label: 'Developer Installs',
      description: 'Trusts the AI suggestion, runs npm install',
      icon: Download,
      color: '#f97316',
    },
    {
      step: 4,
      label: 'Malicious Code Executes',
      description: 'Postinstall scripts exfiltrate secrets, install backdoors',
      icon: Bug,
      color: '#ef4444',
    },
  ]

  return (
    <div
      data-blog-layout='narrow'
      className='my-8 p-6 md:p-8 bg-muted/50 rounded-2xl border border-border/60'
    >
      <div className='text-center mb-6'>
        <h3 className='text-xl md:text-2xl font-bold text-foreground mb-2'>
          SLOPSQUATTING ATTACK CHAIN
        </h3>
        <p className='text-sm text-muted-foreground'>
          How AI hallucinations become real supply chain attacks
        </p>
      </div>

      <div className='flex flex-col items-center gap-4'>
        {steps.map((s, index) => (
          <React.Fragment key={s.step}>
            <div className='w-full max-w-sm p-4 rounded-xl bg-background border border-border/60'>
              <div className='flex items-start gap-3'>
                <div
                  className='w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0'
                  style={{ backgroundColor: `${s.color}20` }}
                >
                  <s.icon className='w-5 h-5' style={{ color: s.color }} />
                </div>
                <div>
                  <div className='flex items-center gap-2'>
                    <span
                      className='text-[10px] font-bold px-1.5 py-0.5 rounded'
                      style={{
                        backgroundColor: `${s.color}20`,
                        color: s.color,
                      }}
                    >
                      STEP {s.step}
                    </span>
                    <span className='text-sm font-semibold text-foreground'>
                      {s.label}
                    </span>
                  </div>
                  <p className='text-[10px] text-muted-foreground mt-1'>
                    {s.description}
                  </p>
                </div>
              </div>
            </div>
            {index < steps.length - 1 && (
              <ArrowDown className='w-4 h-4 text-muted-foreground' />
            )}
          </React.Fragment>
        ))}
      </div>

      {/* Stats */}
      <div className='grid grid-cols-3 gap-3 mt-6'>
        <div className='p-3 rounded-xl bg-[#8b5cf6]/10 border border-[#8b5cf6]/30 text-center'>
          <p className='text-2xl font-bold text-[#8b5cf6]'>58%</p>
          <p className='text-[10px] text-muted-foreground'>
            Hallucinated packages repeated
          </p>
        </div>
        <div className='p-3 rounded-xl bg-[#ef4444]/10 border border-[#ef4444]/30 text-center'>
          <p className='text-2xl font-bold text-[#ef4444]'>~245K</p>
          <p className='text-[10px] text-muted-foreground'>
            Unique fake packages observed
          </p>
        </div>
        <div className='p-3 rounded-xl bg-[#f97316]/10 border border-[#f97316]/30 text-center'>
          <p className='text-2xl font-bold text-[#f97316]'>30%</p>
          <p className='text-[10px] text-muted-foreground'>
            Persist across retries
          </p>
        </div>
      </div>

      <div className='mt-4 px-4 py-3 rounded-xl bg-[#ef4444]/10 border border-[#ef4444]/30'>
        <p className='text-xs md:text-sm text-center text-foreground'>
          <span className='font-semibold text-[#ef4444]'>Critical:</span> Unlike
          typosquatting, slopsquatting packages look perfectly named. Developers
          have no visual signal that the package is fake.
        </p>
      </div>
    </div>
  )
}

/**
 * Defense Layers Diagram — Concentric defense rings
 */
export function DefenseLayersDiagram() {
  const layers = [
    {
      ring: 1,
      name: 'Lockfile Enforcement',
      description: 'Pin known-good versions, block phantom installs',
      tools: ['package-lock.json', 'yarn.lock', 'pip freeze'],
      icon: Lock,
      color: '#008dcc',
    },
    {
      ring: 2,
      name: 'Verification Tools',
      description: 'Validate packages exist and have real maintainers',
      tools: ['Socket.dev', 'npm audit', 'pip-audit'],
      icon: Search,
      color: '#8b5cf6',
    },
    {
      ring: 3,
      name: 'Registry Monitoring',
      description: 'Watch for newly registered hallucinated names',
      tools: ['Lasso Sentinel', 'Snyk Advisor', 'deps.dev'],
      icon: Eye,
      color: '#FDC304',
    },
    {
      ring: 4,
      name: 'Code Review',
      description: 'Human verification of every new dependency',
      tools: ['PR review policy', 'CODEOWNERS', 'Hooks'],
      icon: ShieldCheck,
      color: '#10b981',
    },
  ]

  return (
    <div
      data-blog-layout='narrow'
      className='my-8 p-6 md:p-8 bg-muted/50 rounded-2xl border border-border/60'
    >
      <div className='text-center mb-6'>
        <h3 className='text-xl md:text-2xl font-bold text-foreground mb-2'>
          DEFENSE IN DEPTH
        </h3>
        <p className='text-sm text-muted-foreground'>
          Four layers of protection against AI supply chain attacks
        </p>
      </div>

      <div className='flex flex-col gap-3'>
        {layers.map(layer => (
          <div
            key={layer.name}
            className='p-4 rounded-xl bg-background border-l-4 border border-border/60'
            style={{ borderLeftColor: layer.color }}
          >
            <div className='flex items-start gap-3'>
              <div
                className='w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0'
                style={{ backgroundColor: `${layer.color}20` }}
              >
                <layer.icon
                  className='w-5 h-5'
                  style={{ color: layer.color }}
                />
              </div>
              <div className='flex-1 min-w-0'>
                <div className='flex items-center gap-2'>
                  <span
                    className='text-[10px] font-bold px-1.5 py-0.5 rounded-full'
                    style={{
                      backgroundColor: `${layer.color}20`,
                      color: layer.color,
                    }}
                  >
                    LAYER {layer.ring}
                  </span>
                  <span className='text-sm font-semibold text-foreground'>
                    {layer.name}
                  </span>
                </div>
                <p className='text-[10px] text-muted-foreground mt-1'>
                  {layer.description}
                </p>
                <div className='flex flex-wrap gap-1 mt-2'>
                  {layer.tools.map(tool => (
                    <span
                      key={tool}
                      className='text-[9px] px-2 py-0.5 rounded-full bg-muted border border-border/60 text-muted-foreground'
                    >
                      {tool}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className='mt-4 px-4 py-3 rounded-xl bg-[#10b981]/10 border border-[#10b981]/30'>
        <p className='text-xs md:text-sm text-center text-foreground'>
          <span className='font-semibold text-green-600 dark:text-green-400'>
            Defense principle:
          </span>{' '}
          No single layer is sufficient. Combine lockfile enforcement with
          automated verification and human review for comprehensive protection.
        </p>
      </div>
    </div>
  )
}

/**
 * Supply Chain Threat Landscape Diagram
 */
export function SupplyChainThreatLandscapeDiagram() {
  const threats = [
    {
      name: 'Typosquatting',
      description: 'Register misspelled package names',
      example: 'lodash → lodahs',
      detection: 'Moderate',
      detectionColor: '#FDC304',
      aiAmplified: false,
      severity: 'High',
      icon: FileWarning,
    },
    {
      name: 'Slopsquatting',
      description: 'Register AI-hallucinated package names',
      example: 'AI suggests "react-auth-helper"',
      detection: 'Very Hard',
      detectionColor: '#ef4444',
      aiAmplified: true,
      severity: 'Critical',
      icon: Bot,
    },
    {
      name: 'Dependency Confusion',
      description: 'Public package overrides private name',
      example: 'Internal "@corp/utils" spoofed',
      detection: 'Moderate',
      detectionColor: '#FDC304',
      aiAmplified: false,
      severity: 'High',
      icon: Package,
    },
    {
      name: 'Star Jacking',
      description: 'Fake GitHub stars/popularity signals',
      example: 'Purchased social proof',
      detection: 'Hard',
      detectionColor: '#f97316',
      aiAmplified: true,
      severity: 'Medium',
      icon: AlertTriangle,
    },
  ]

  return (
    <div
      data-blog-layout='narrow'
      className='my-8 p-6 md:p-8 bg-muted/50 rounded-2xl border border-border/60'
    >
      <div className='text-center mb-6'>
        <h3 className='text-xl md:text-2xl font-bold text-foreground mb-2'>
          SUPPLY CHAIN THREAT LANDSCAPE
        </h3>
        <p className='text-sm text-muted-foreground'>
          How slopsquatting compares to other package attacks
        </p>
      </div>

      <div className='space-y-3'>
        {threats.map(threat => (
          <div
            key={threat.name}
            className='p-4 rounded-xl bg-background border border-border/60'
          >
            <div className='flex items-start gap-3'>
              <div
                className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${
                  threat.aiAmplified ? 'bg-[#F90B8A]/20' : 'bg-muted'
                }`}
              >
                <threat.icon
                  className='w-5 h-5'
                  style={{
                    color: threat.aiAmplified ? '#F90B8A' : '#666',
                  }}
                />
              </div>
              <div className='flex-1 min-w-0'>
                <div className='flex items-center gap-2 flex-wrap'>
                  <span className='text-sm font-semibold text-foreground'>
                    {threat.name}
                  </span>
                  {threat.aiAmplified && (
                    <span className='text-[9px] font-bold px-1.5 py-0.5 rounded-full bg-[#F90B8A]/20 text-[#F90B8A]'>
                      AI-AMPLIFIED
                    </span>
                  )}
                  <span
                    className={`text-[9px] font-bold px-1.5 py-0.5 rounded-full ml-auto ${
                      threat.severity === 'Critical'
                        ? 'bg-[#ef4444]/20 text-[#ef4444]'
                        : threat.severity === 'High'
                          ? 'bg-[#f97316]/20 text-[#f97316]'
                          : 'bg-[#FDC304]/20 text-[#FDC304]'
                    }`}
                  >
                    {threat.severity}
                  </span>
                </div>
                <p className='text-[10px] text-muted-foreground mt-1'>
                  {threat.description}
                </p>
                <div className='flex items-center gap-4 mt-2'>
                  <span className='text-[10px] text-muted-foreground'>
                    Example:{' '}
                    <span className='font-mono text-foreground'>
                      {threat.example}
                    </span>
                  </span>
                  <span className='text-[10px] ml-auto'>
                    Detection:{' '}
                    <span
                      className='font-bold'
                      style={{ color: threat.detectionColor }}
                    >
                      {threat.detection}
                    </span>
                  </span>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className='mt-4 px-4 py-3 rounded-xl bg-[#F90B8A]/10 border border-[#F90B8A]/30'>
        <p className='text-xs md:text-sm text-center text-foreground'>
          <span className='font-semibold text-[#F90B8A]'>
            New threat class:
          </span>{' '}
          Slopsquatting is uniquely dangerous because the package names look
          perfectly legitimate. Traditional typo detection tools cannot catch
          them.
        </p>
      </div>
    </div>
  )
}
